import logger from '../log';
import { ITechApi } from '@kollective-frontend/ksdk-api';
import { CleanupMethod } from '../player/BaseInteraction';

const log = logger.getLogger(__filename);
export type UUFunction = (...item: unknown[]) => unknown;

export class TechApi implements ITechApi {
  private techMap: Map<string, TechApiFunction>;
  constructor() {
    this.techMap = new Map();
  }

  getTechApiFunction(name: string): TechApiFunction {
    let techApiFunction = this.techMap.get(name);
    if (!techApiFunction) {
      techApiFunction = new TechApiFunction(name);
      this.techMap.set(name, techApiFunction);
    }
    return techApiFunction;
  }
  get(name: string): UUFunction {
    const item = this.getTechApiFunction(name);
    return (...items: unknown[]): unknown => {
      return item.func(...items);
    };
  }
  detach(name: string): void {
    this.getTechApiFunction(name).unset();
  }
  get size(): number {
    return this.techMap.size;
  }
}

export class TechApiFunction {
  readonly name: string;
  private _func?: UUFunction;
  constructor(name: string) {
    this.name = name;
  }

  setFunction(f: UUFunction): void {
    this._func = f;
  }

  has(): boolean {
    return !!this._func;
  }

  unset(): void {
    this._func = undefined;
  }

  func(...args: unknown[]): unknown {
    log.trace('Calling tech api for:', this.name, { func: this._func, args });
    if (this._func) {
      return this._func(...args);
    } else {
      return undefined;
    }
  }
}

export type TechDataFunc<T> = () => T | undefined;

export function setupTechApiFunction<T>(
  tech: TechApi,
  name: string,
  func: TechDataFunc<T>,
  onlyIfNotSet?: boolean
) {
  const taf = tech.getTechApiFunction(name);
  if (onlyIfNotSet && typeof taf.has === 'function' && taf.has()) {
    return;
  }
  taf.setFunction(func);
}

/**
 * Unbind a kollective tech function
 *
 * @param tech - kollective tech object to unbind the handler
 * @param name - name of the tech function
 */
export function tearDownTechApiFunction(tech: TechApi, name: string) {
  const taf = tech.getTechApiFunction(name);
  if (typeof taf.unset !== 'function') {
    // We're dealing with an older tech
    (taf as unknown as Record<string, unknown>)._func = undefined;
  } else {
    taf.unset();
  }
}

/**
 * Bind a kollective tech function and its tear down method
 *
 * @param techApi - kollective tech object to bind the handler.
 * @param name - name of the tech function
 * @param teardownMethods - the object to register the teardown method
 * @param handler - handler for the tech function
 */
export function bindTechApiFunction<T>(
  tech: TechApi,
  name: string,
  teardownMap: Record<string, CleanupMethod>,
  handler: TechDataFunc<T>,
  onlyIfNotSet?: boolean
): void {
  setupTechApiFunction(tech, name, handler, onlyIfNotSet);
  teardownMap[name] = () => tearDownTechApiFunction(tech, name);
}

export function getValueFromTech(
  tech: ITechApi | undefined,
  name: string,
  ...args: unknown[]
): unknown {
  const func = (tech ?? window.ksdk?.api?.tech)?.get(name);
  if (typeof func === 'function') {
    return (func as UUFunction)(...args);
  }
}
